import {inject, NewInstance, PLATFORM} from 'aurelia-framework';
import {handleResponse, preserveDummyRequest} from '../../tools/http/http';
import {HttpClient} from 'aurelia-http-client';
import {I18N} from 'aurelia-i18n';
import {get, noop, last, nth, castArray} from 'lodash';
import {GroupEventBus} from '../../broadcast/group-event-bus';
import {ContentService} from '../content-service';
import {resolveIf} from '../../tools/promise';
import {
  AcceptEvent,
  DeclineEvent,
  JoinEvent,
  LeaveEvent,
  RemoveEvent,
  EditEvent,
  CreateEvent,
  RequestAffiliationEvent,
  SelectEvent
} from '../../broadcast/group-events';

@inject(NewInstance.of(HttpClient), I18N, GroupEventBus, ContentService)
export class GroupService {

  constructor(private __httpClient: HttpClient, private __i18n: I18N, private __groupEventBus: GroupEventBus, private __contentService: ContentService) {
    this.__httpClient = __httpClient.configure(x => {
      x.withBaseUrl('fe/group/');
      x.withHeader('pragma', 'no-cache');
      x.withHeader('cache-control', 'no-cache');
    });
  }

  /**
   * @param {number} [groupId]
   * @param {string} [groupGuid]
   * @return {Promise.<Object>}
   */
  info({groupId, groupGuid}: {groupId?: number, groupGuid?: string}) {
    return handleResponse(this.__httpClient.post('info', {groupId, groupGuid}));
  }

  /**
   * @param {number} groupId
   * @param {boolean} [global=false] - Indicates if current user is treated as global member
   * @return {Array.<Promise>}
   * @public
   */
  isMember({groupId, global = false}) {
    if (groupId < 0) {
      return preserveDummyRequest(Promise.resolve(true));
    }
    return handleResponse(this.__httpClient.get(`is-member/${groupId}/${global}`),
        promise => promise.then(response => response.isMember));
  }

  /**
   * @public
   * @returns {Promise}
   */
  defaultGroup() {
    return handleResponse(this.__httpClient.get('default'));
  }

  /**
   * @param {boolean} [withDefaultGroups=true]
   * @public
   * @returns {Array.<Promise.<Array.<Object>>>}
   */
  rootList(withDefaultGroups = true) {
    return handleResponse(this.__httpClient.get(`root-list/${withDefaultGroups}`));
  }

  /**
  * @public
  * @returns {Array.<Promise.<Array.<Object>>>}
  */
  availableGroupsList() {
    return handleResponse(this.__httpClient.get('group-list'));
  }

  /**
   * @param {number} groupId
   * @public
   * @returns {Array.<Promise.<Array.<Object>>>}
   */
  childList(groupId = defaultGroupId.PUBLIC) {
    return handleResponse(this.__httpClient.get(`child-list/${groupId}`));
  }

  /**
   * @param {number} groupId
   * @param {number} position
   * @return {Promise}
   */
  setGroupPosition(groupId, position) {
    return this.__httpClient.createRequest('position').asPost()
      .withHeader('Content-Type', 'application/json')
      .withContent(JSON.stringify({groupId, position}))
      .send();
  }

  /**
   * @param {number} groupId
   * @public
   * @returns {Array.<Promise.<Array.<Object>>>}
   */
  parentPath(groupId = defaultGroupId.PUBLIC) {
    return handleResponse(this.__httpClient.get(`parent-path/${groupId}`), promise => {
      // TODO: don't modify data that way. Better use translation directly in view for handling translation signal (language switch on the fly)
      return promise.then(path => {
        for (let item of path) {
          if (item.id < 0) {
            if (item.type) {
              item.type = this.__i18n.tr((`groups_select_wide.groups.${item.type}`).toLowerCase());
            }
            if (item.name) {
              item.name = this.__i18n.tr((`groups_select_wide.groups.${item.name}`).toLowerCase());
            }
          }
        }

        return path;
      });
    });
  }

  /**
   * @param {number} groupId
   * @param {Object} [path]
   * @returns {Promise.<Object>}
   * @public
   */
  withPath(groupId: number, path?: Object) {
    return path ? Promise.resolve(path) : this.parentPath(groupId).response;
  }

  /**
   * @param {number} groupId
   * @public
   * @returns {Array.<Promise.<Array.<Object>>>}
   */
  affiliationCandidates(groupId = defaultGroupId.PUBLIC) {
    if (!isGroupDefaultOrDummy(groupId)) {
      return handleResponse(this.__httpClient.get(`affiliate-candidate-list/${groupId}`));
    }
    return preserveDummyRequest(Promise.resolve([]));
  }

  /**
   * @public
   */
  createRootGroup() {
    return this.__contentService.showGroupSettings(defaultGroupId.PUBLIC);
  }

  /**
   * @param {number} parentId
   * @public
   */
  createSubGroup(parentId) {
    if (!isGroupDefaultOrDummy(parentId)) {
      PLATFORM.global.createGroupLinkage(parentId, -1);
    }
  }

  /**
   * @param {number} groupId
   * @param {*} [caller]
   * @param {Array} [path]
   * @returns {Promise}
   * @public
   */
  selectGroup({groupId, caller, path}: {groupId?: number, caller?: any, path?: any[]} = {}) {
    get(PLATFORM.global.groupEdit3GoAway, 'unload', noop)();
    return get(PLATFORM.global.groupEdit3GoAway, 'promise', Promise.resolve())
      .then(() => this.withPath(groupId, path)).then(path => {
        //remove top-level groups where the user has no access
        while (path.length > 1 && !path[0].isMember && !path[0].isGlobalAdmin) {
          path.shift()
        } 
        PLATFORM.global.Widget.Grid.update({groupId});
        PLATFORM.global.Themes.refreshTheme();
        this.__groupEventBus.publish(new SelectEvent(groupId, path, caller));
      });
  }

  /**
   * @param {*} [caller]
   * @public
   * @returns {Promise}
   */
  showMyStuff({caller}: {caller?: any} = {}) {
    return this.__contentService.showHome()
      .then(() => this.selectGroup({groupId: defaultGroupId.PUBLIC, caller}));
  }

  /**
   * @param {number} groupId
   * @param {string} groupGuid
   * @param {*} [caller]
   * @public
   * @returns {Promise}
   */
  showGroup({groupId, groupGuid, caller}) {
    return this.__contentService.showGroupDirectory(groupGuid)
      .then(() => {
          return this.selectGroup({groupId, caller});
      });
  }

  /**
   * @param {number} groupId
   * @param {*} [caller]
   * @public
   * @returns {Promise}
   */
  editGroup({groupId, caller}) {
    return this.__contentService.showGroupSettings(groupId)
      .then(() => this.selectGroup({groupId, caller}));
  }

  /**
   * @param {number} groupId
   * @param {boolean} [isNew]
   * @param {*} [caller]
   * @public
   * @returns {Promise}
   */
  saveEdit({groupId, isNew, caller}: {groupId: number, isNew: boolean, caller: any}) {
    return this.info({groupId}).response.then(info => {
      isNew = isNew !== undefined ? isNew : info.isNew;
      return this.editGroup({groupId, caller}).then(() => {
        if (isNew) {
          return this.withPath(groupId).then(path => {
            this.__groupEventBus.publish(new CreateEvent(groupId, path, caller));
          });
        }
        return this.info({groupId}).response.then(info => {
          this.__groupEventBus.publish(new EditEvent(groupId, info, caller));
        });
      });
    });
  }

  /**
   * @param {number} groupId
   * @param {boolean} [eventOnly = false]
   * @param {*} [caller]
   * @return {Promise}
   * @public
   */
  acceptGroup({groupId, eventOnly = false, caller}) {
    return (!eventOnly ? this.info({groupId}).response.then(info => {
      return new Promise(resolve => {
        PLATFORM.global.acceptGroup(groupId, info.name, resolve);
      });
    }) : Promise.resolve()).then(() => this.withPath(groupId)).then(path => {
      this.__groupEventBus.publish(new AcceptEvent(groupId, path, caller));
    });
  }

  /**
   * @param {number} groupId
   * @param {boolean} [eventOnly = false]
   * @param {*} [caller]
   * @return {Promise}
   * @public
   */
  declineGroup({groupId, eventOnly = false, caller}) {
    return (!eventOnly ? this.info({groupId}).response.then(info => {
      let {name} = info;
      return new Promise(resolve => {
        PLATFORM.global.declineGroup(groupId, name, resolve);
      });
    }) : Promise.resolve()).then(() => {
      this.__groupEventBus.publish(new DeclineEvent(groupId, caller));
    });
  }

  /**
   * @param {number} groupId
   * @param {*} [caller]
   * @return {Promise}
   * @public
   */
  leaveGroup({groupId, caller}: {groupId: number, caller?: any}) {
    return this.withPath(groupId).then(path => {
      let {name, isLastAdmin} = last(path);
      return new Promise(resolve => {
        PLATFORM.global.leaveGroup(groupId, name, isLastAdmin, resolve);
      }).then(() => {
        this.__groupEventBus.publish(new LeaveEvent(groupId, path, caller));
      });
    });
  }

  /**
   *
   * @param {number} groupId
   * @param {*} [caller]
   * @return {Promise}
   * @public
   */
  deleteGroup({groupId, caller}) {
    return this.withPath(groupId).then(path => {
      // @ts-ignore
      let {guid} = nth(-2) || {};
      let {name} = last(path) || {};
      return new Promise(resolve => {
        PLATFORM.global.deleteGroup(groupId, name, guid, resolve);
      }).then(() => {
        this.__groupEventBus.publish(new RemoveEvent(groupId, path, caller));
      });
    });
  }

  /**
   * @param {number} groupId
   * @public
   */
  addGroupMembers(groupId) {
    if (!isGroupDefaultOrDummy(groupId)) {
      PLATFORM.global.addGroupMembers(groupId);
    }
  }

  /**
   * @param {number} groupId
   * @param {number|Array.<number>} groupIds
   * @public
   */
  requestAffiliation(groupId = defaultGroupId.PUBLIC, groupIds) {
    let ids = castArray(groupIds).filter(id => !isGroupDefaultOrDummy(id));
    return resolveIf(
      !isGroupDefaultOrDummy(groupId) && Boolean(ids.length),
      resolve => PLATFORM.global.saveGroupProperty(groupId, 'affiliate', ids, resolve)
    );
  }

  /**
   * @param {number} groupId
   * @public
   */
  affiliateWith(groupId = defaultGroupId.PUBLIC) {
    if (!isGroupDefaultOrDummy(groupId)) {
      this.__groupEventBus.publish(new RequestAffiliationEvent(groupId, this));
    }
  }

  /**
   * @param {number} groupId
   * @returns {Promise}
   * @public
   */
  join(groupId = defaultGroupId.PUBLIC) {
    return resolveIf(!isGroupDefaultOrDummy(groupId), resolve => {
      PLATFORM.global.joinGroup(resolve);
    }).then(() => this.withPath(groupId)).then(path => {
      this.__groupEventBus.publish(new JoinEvent(groupId, path, this));
    });
  }

  /**
   * @param {number} groupId
   * @param {Array.<string>} emails
   * @public
  */
  inviteMembers(groupId = defaultGroupId.PUBLIC, emails) {
    // let emailList = " ";
    // for(let i=0;i<emails.length;i++){
    //   if(i < (emails.length - 1)){
    //     emailList += (emails[i] + ',');
    //   }else{
    //
    //     emailList += emails[i];
    //   }
    // }
    // return resolveIf(
    //   !isGroupDefaultOrDummy(groupId) && emails.length,
    //   resolve => {
    //     PLATFORM.global.saveGroupProperty(groupId, 'invite_users', emails, ()=>{
    //       this.__groupEventBus.publish(new InviteEvent(groupId, this))
    //     });
    // });
    let formData = new FormData();
    formData.append('invite_users', emails);
    let httpClientAux = new HttpClient();

    return handleResponse(httpClientAux.post('groupsavepart/' + groupId, formData));
  }

  /**
   * @param {number} groupId
   * @param {Array.<string>} emails
   * @param {boolean} sendNotice
   * @public
   */
  onboardMembers(groupId = defaultGroupId.PUBLIC, emails, sendNotice) {
    let formData = new FormData();
    formData.append('onboard_users', emails);
    formData.append('isSendNotice', sendNotice);
    let httpClientAux = new HttpClient();

    return handleResponse(httpClientAux.post('groupsavepart/' + groupId, formData ));
  }

  /**
   * @param {number} groupId
   * @public
   */
  makeDefaultGroup(groupId = defaultGroupId.PUBLIC) {
    let formData = new FormData();
    // @ts-ignore
    formData.append('def_group', true);
    let httpClientAux = new HttpClient();

    return handleResponse(httpClientAux.post('groupsavepart/' + groupId, formData ));
  }

  /**
   * @param {file} file
   * @public
   */
  uploadFile(file) {
    let formData = new FormData();
    formData.append('invite_type', 'bulk');
    formData.append('upload_blk_csv', file);
    let httpClientAux = new HttpClient();

    return handleResponse(httpClientAux.post('upload', formData ));
  }

  /**
   * @param {number} groupId
   * @param {string} fileName
   * @public
   */
  preParseBulkOnboard(groupId, fileName) {
    let httpClientAux = new HttpClient();

    return handleResponse(httpClientAux.get(`preparse_onboardbulk/${groupId}?fileName=${fileName}`));
  }

  /**
   * @param {number} groupId
   * @param {string} fileName
   * @public
   */
  preParseBulkInvite(groupId, fileName) {
    let httpClientAux = new HttpClient();

    return handleResponse(httpClientAux.get(`preparse_invitebulk/${groupId}?fileName=${fileName}`));
  }

  /**
   * @param {number} groupId
   * @param {file} file
   * @public
   */
  onboardFromFile(groupId, file, sendInvite) {
    let formData = new FormData();
    formData.append('invite_type', 'bulk');
    formData.append('upload_blk_csv', file);
    if (sendInvite) {
      formData.append('isSendNotice', 'on');
    }
    let httpClientAux = new HttpClient();

    return handleResponse(httpClientAux.post(`onboard_bulk/${groupId}`, formData ));
  }

  /**
   * @param {number} groupId
   * @param {file} file
   * @public
   */
  inviteFromFile(groupId, file) {
    let formData = new FormData();
    formData.append('invite_type', 'bulk');
    formData.append('upload_blk_csv', file);
    let httpClientAux = new HttpClient();

    return handleResponse(httpClientAux.post(`invite_bulk/${groupId}`, formData ));
  }

  /**
   * @param {number} groupId
   * @return {Object}
   * @public
   */
  reports(groupId) {
    return handleResponse(this.__httpClient.get(`reports/${groupId}`));
  }
}

export const defaultGroupId = Object.freeze({
  PUBLIC: -1,
  CONNECTION: -2,
  DEFAULT: -3
});

export function isGroupDefault(groupId?: number) {
  return groupId && groupId < 0;
}

export function isGroupDefaultOrDummy(groupId: number) {
  return !groupId || isGroupDefault(groupId);
}
